import React, { useEffect, useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { sessionService } from '../../services/sessionService';

const ProtectedRoute = ({ children, allowedRole = null }) => {
    const location = useLocation();
    const navigate = useNavigate();
    
    const handleSessionExpiration = useCallback(() => {
        navigate('/login', { state: { from: location.pathname } }, { replace: true });
    }, [navigate, location.pathname]);

    const checkSession = useCallback(() => {
        const isValid = sessionService.isSessionValid();
        if (isValid) {
            sessionService.updateLastActivity();
        }
        return isValid;
    }, []);

    useEffect(() => {
        // Start session check when component mounts
        sessionService.startSessionCheck(handleSessionExpiration);

        // Add activity listeners
        const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
        const handleUserActivity = () => {
            if (checkSession()) {
                sessionService.updateLastActivity();
            }
        };

        activityEvents.forEach(event => {
            window.addEventListener(event, handleUserActivity);
        });

        return () => {
            sessionService.stopSessionCheck();
            activityEvents.forEach(event => {
                window.removeEventListener(event, handleUserActivity);
            });
        };
    }, [checkSession, handleSessionExpiration]);

    // Check token existence
    if (!sessionService.getToken()) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Check session validity
    if (!sessionService.isSessionValid()) {
        sessionService.logout().then(() => {
            return <Navigate to="/login" state={{ from: location }} replace />;
        });
    }

    // Check role if specified
    if (allowedRole && sessionService.getUserRole() !== allowedRole) {
        return <Navigate to="/unauthorized" replace />;
    }

    return children;
};

export default ProtectedRoute;